import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CotinSidebar,
    type FavoriteItem,
    type SidebarMenuItem,
} from '@cotin/biblioteca-componentes-react';
import { useSidebar } from '@/core/context/sidebar/SidebarContext';
import { SidebarItem } from '@/core/context/sidebar/sidebar.types';
import { findActiveItemId } from '@/core/context/sidebar/sidebar.mapper';
import { normalizeSidebarHref } from '@/core/context/sidebar/sidebar.utils';

const isMobileViewport = () =>
    typeof window !== 'undefined' && window.innerWidth <= 768;

const Sidebar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        isCollapsed,
        isMobileOpen,
        menuItems: tree,
        favoriteShortcutIds,
        availableShortcuts,
        expandSidebar,
        collapseSidebar,
        openMobileSidebar,
        closeMobileSidebar,
        isShortcutFavorite,
        toggleFavoriteShortcut,
    } = useSidebar();

    const currentPath = location.pathname + location.search;
    const activeItemId = useMemo(
        () => findActiveItemId(tree, currentPath),
        [currentPath, tree]
    );

    const menuItems = useMemo(() => {
        const toCotinItems = (items: SidebarItem[]): SidebarMenuItem[] =>
            items.map((item) => ({
                id: item.id,
                label: item.label,
                icon: item.icon || undefined,
                href: item.route || undefined,
                active: activeItemId === item.id,
                onClick: item.route
                    ? () => {
                          navigate(normalizeSidebarHref(item.route));
                          if (isMobileViewport() && isMobileOpen) {
                              closeMobileSidebar();
                          }
                      }
                    : undefined,
                children: item.children.length > 0 ? toCotinItems(item.children) : undefined,
            }));

        return toCotinItems(tree);
    }, [activeItemId, closeMobileSidebar, isMobileOpen, navigate, tree]);

    const favoriteItems = useMemo((): FavoriteItem[] => {
        const byId = new Map(availableShortcuts.map((item) => [item.id, item]));
        return favoriteShortcutIds
            .map((id) => byId.get(id))
            .filter((item): item is SidebarItem => !!item?.route)
            .map((item) => ({
                id: item.id,
                title: item.label,
                icon: item.icon || undefined,
            }));
    }, [availableShortcuts, favoriteShortcutIds]);

    const sidebarExpanded = isMobileViewport() ? isMobileOpen : !isCollapsed;
    const hostCollapsed = isCollapsed && !isMobileViewport();

    const onSidebarToggle = (expanded: boolean) => {
        if (isMobileViewport()) {
            if (expanded) {
                openMobileSidebar();
                expandSidebar();
            } else {
                closeMobileSidebar();
            }
            return;
        }

        if (expanded) {
            expandSidebar();
        } else {
            collapseSidebar();
        }
    };

    const onFavoriteChange = (itemId: string, isFavorite: boolean) => {
        const item = availableShortcuts.find((shortcut) => shortcut.id === itemId);
        if (!item?.route) {
            return;
        }
        if (isShortcutFavorite(item) !== isFavorite) {
            toggleFavoriteShortcut(item);
        }
    };

    return (
        <CotinSidebar
            id="app-cotin-sidebar"
            className={`app-sidebar-host${hostCollapsed ? ' sidebar-host-collapsed' : ''}`}
            items={menuItems}
            isExpanded={sidebarExpanded}
            activeItemId={activeItemId}
            enableFavorites
            favorites={favoriteItems}
            onToggle={onSidebarToggle}
            onFavoriteChange={onFavoriteChange}
        />
    );
};

export default Sidebar;
